// Lightweight JWT inspection — no signature verification, no dependency.
//
// Used by the SSE token-refresh helper (and ensureFreshToken) to decide
// whether the access token needs a proactive refresh before opening an
// EventSource. The browser's native EventSource can't carry an
// Authorization header, so a stale token baked into the `?token=` query
// param would otherwise survive every native reconnect.

/**
 * Decodes the `exp` claim from a JWT without verifying the signature.
 * Returns the expiry as a Unix timestamp (seconds), or null if unreadable.
 */
export function getJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const segment = parts[1];
    if (!segment) return null;
    // base64url → base64, then pad to a multiple of 4 for atob.
    const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const json = atob(padded);
    const parsed = JSON.parse(json) as Record<string, unknown>;
    return typeof parsed['exp'] === 'number' ? parsed['exp'] : null;
  } catch {
    return null;
  }
}

/**
 * Returns true if the token is expired or within `skewSeconds` of expiry.
 * Fails safe: if `exp` is unreadable, returns false (treat as valid and let
 * the server be the authority).
 */
export function isTokenExpiredOrExpiring(token: string, skewSeconds = 30): boolean {
  const exp = getJwtExp(token);
  if (exp === null) return false; // unreadable → open with current token
  return Date.now() / 1000 > exp - skewSeconds;
}
